import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, Sparkles, Pause, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { DocUploadDialog } from "@/components/docs/DocUploadDialog";
import { useDocsMeta } from "@/pages/Documenti";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";

const JOB_LABEL = { in_corso: "In corso", completata: "Completata", sospesa: "Sospesa", errore: "Interrotta per errore" };

const Extraction = ({ d, reload }) => {
  const j = d.job;
  useEffect(() => {
    if (j?.status !== "in_corso") return undefined;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [j?.status, reload]);
  const call = async (path) => {
    try {
      await api.post(`/documents/${d.id}/${path}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pct = j ? Math.round((j.chunks_done / Math.max(j.chunks_total, 1)) * 100) : 0;
  return (
    <section className="card-soft p-5" data-testid="doc-extraction">
      <h3 className="flex items-center gap-2 font-bold"><Sparkles className="h-4 w-4 text-primary" />Estrazione obblighi con Toth</h3>
      {j ? (
        <div className="mt-3 space-y-2">
          <Progress value={pct} />
          <p className="text-sm" data-testid="doc-extraction-status"><b>{JOB_LABEL[j.status] || j.status}</b> · pagine {j.pages_done}/{j.pages_total} · {j.found} bozze trovate {j.note && `· ${j.note}`}</p>
        </div>
      ) : <p className="mt-2 text-sm text-muted-foreground">Toth legge il documento a blocchi e propone le bozze degli obblighi: diventano attivi solo dopo la convalida.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {d.can.extract && j?.status !== "in_corso" && j?.status !== "completata" && <Button onClick={() => call("extract")} className="rounded-xl" data-testid="doc-extract-start">{j ? "Riprendi estrazione" : "Avvia estrazione"}</Button>}
        {d.can.extract && j?.status === "in_corso" && <Button variant="outline" onClick={() => call("extract/stop")} className="rounded-xl" data-testid="doc-extract-stop"><Pause className="mr-1 h-4 w-4" />Sospendi</Button>}
        {j?.found > 0 && <Link to={`/obblighi?doc=${d.id}`} className="inline-flex h-10 items-center rounded-xl border px-4 text-sm font-semibold" data-testid="doc-obligations-link">Vedi obblighi</Link>}
      </div>
    </section>
  );
};

const PageViewer = ({ d }) => {
  const [sp, setSp] = useSearchParams();
  const page = Math.min(Math.max(parseInt(sp.get("page") || "1", 10) || 1, 1), d.pages);
  const [text, setText] = useState("");
  useEffect(() => {
    api.get(`/documents/${d.id}/pages/${page}`).then((r) => setText(r.data.text)).catch(() => setText(""));
  }, [d.id, page]);
  const go = (p) => setSp({ page: String(Math.min(Math.max(p, 1), d.pages)) });
  const openPdf = async () => {
    try {
      const { data } = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
      window.open(`${URL.createObjectURL(data)}#page=${page}`, "_blank", "noopener");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="card-soft p-5" data-testid="doc-page-viewer">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" onClick={() => go(page - 1)} className="rounded-xl" aria-label="Pagina precedente" data-testid="doc-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Input type="number" value={page} min={1} max={d.pages} onChange={(e) => go(parseInt(e.target.value, 10) || 1)} className="w-20 rounded-xl" data-testid="doc-page-input" />
        <span className="text-sm text-muted-foreground">di {d.pages}</span>
        <Button size="icon" variant="outline" onClick={() => go(page + 1)} className="rounded-xl" aria-label="Pagina successiva" data-testid="doc-page-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={openPdf} className="ml-auto rounded-xl" data-testid="doc-open-pdf"><ExternalLink className="mr-1 h-4 w-4" />Apri PDF a p. {page}</Button>
      </div>
      <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-xl bg-muted p-4 font-sans text-sm leading-relaxed" data-testid="doc-page-text">{text || "Pagina senza testo estraibile (es. immagine)."}</pre>
    </section>
  );
};

export default function DocumentoDetail() {
  const { id } = useParams();
  const meta = useDocsMeta();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [up, setUp] = useState(false);
  const load = useCallback(() => {
    api.get(`/documents/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (err) return <p className="card-soft p-8 text-center font-bold text-red-600" data-testid="doc-error">{err}</p>;
  if (!d || !meta) return <p className="text-muted-foreground">Caricamento…</p>;
  return (
    <div data-testid="documento-page">
      <Link to="/documenti" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary"><ArrowLeft className="h-4 w-4" />Documenti</Link>
      <div className="card-soft mb-5 p-5 sm:p-6">
        <h1 className="text-2xl font-extrabold sm:text-3xl" data-testid="doc-title">{d.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{meta.types[d.doc_type]} · versione {d.version}{!d.is_current && " (superata)"} · {d.pages} pagine · caricato da {d.uploaded_by_name} il {fmtDT(d.created_at)}</p>
        <p className="mt-1 break-all text-[11px] text-muted-foreground">SHA-256 {d.sha256}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {d.versions.length > 1 && d.versions.map((v) => <Link key={v.id} to={`/documenti/${v.id}`} className={`rounded-full border px-3 py-1 text-xs font-semibold ${v.id === d.id ? "bg-accent" : ""}`} data-testid={`doc-version-${v.version}`}>v{v.version} · {fmtDT(v.created_at)}</Link>)}
          {d.can.upload && d.is_current && <Button size="sm" variant="outline" onClick={() => setUp(true)} className="rounded-xl" data-testid="doc-new-version"><Upload className="mr-1 h-4 w-4" />Nuova versione</Button>}
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <PageViewer d={d} />
        <Extraction d={d} reload={load} />
      </div>
      <DocUploadDialog open={up} onOpenChange={setUp} types={meta.types} replaces={d} onDone={(n) => window.location.assign(`/documenti/${n.id}`)} />
    </div>
  );
}
